// Openings (windows & doors) on the house wall-graph (Stage 9c).
//
// An opening sits on a wall, its CENTRE at `offsetM` along the wall measured
// from the wall's `a` endpoint, spanning `widthM`:
//   { id, wallId, offsetM, widthM, kind:"window"|"door" }
// Stored per floor (floor.openings), persisted with the design. Pure JS, no
// DOM/Konva — unit-tested with Vitest. All mutators return NEW arrays; inputs
// are never modified.
//
// WHY RECONCILIATION IS NEEDED: wall ids are NOT stable. addRoomRect and
// settleVertex re-run normalizeGraph, which splits/re-creates walls with fresh
// ids (see graph.js). An opening anchored to a wallId alone would orphan on the
// next structural edit. So after every structural change we re-attach each
// opening (reconcileOpenings):
//   * if its wall id still exists (a vertex drag stretched the SAME wall) we
//     keep it there, preserving its position RATIO along the wall;
//   * otherwise (the wall was split/renamed by normalize) we re-anchor it to
//     whichever wall now passes through its old CENTRE — mirroring how
//     faces.reconcileRooms carries room metadata by centroid.
//
// Coordinates are the house's LOCAL plan frame (x east, y south), same as
// graph.js. windowSunHours works in the PLOT frame (also east/south); the
// caller rotates the local outward normal by the object's rotation first.

export const WINDOW_WIDTH_M = 1.2
export const DOOR_WIDTH_M = 0.9
export const MIN_OPENING_M = 0.4

export const OPENING_KINDS = [
  { key: "window", label: "Окно" },
  { key: "door", label: "Дверь" },
]

const DEFAULT_WIDTH = { window: WINDOW_WIDTH_M, door: DOOR_WIDTH_M }

// A centre this close to a wall counts as "on" it during reconciliation. Well
// above the 0.01 m on-segment tolerance graph.js uses for T-splits, so an
// opening reliably re-attaches to the sub-wall it was split onto.
const ATTACH_TOL_M = 0.15

function vById(graph) {
  return new Map((graph?.vertices ?? []).map((v) => [v.id, v]))
}

// Geometry of a wall: endpoints, length and unit direction a→b. null when the
// wall is missing or degenerate.
function wallEnds(graph, wallId, byId = vById(graph)) {
  const w = (graph?.walls ?? []).find((x) => x.id === wallId)
  if (!w) return null
  const a = byId.get(w.a)
  const b = byId.get(w.b)
  if (!a || !b) return null
  const len = Math.hypot(b.xM - a.xM, b.yM - a.yM)
  if (len < 1e-9) return null
  return { a: [a.xM, a.yM], b: [b.xM, b.yM], len, ux: (b.xM - a.xM) / len, uy: (b.yM - a.yM) / len }
}

// Clamp an opening's [offset−w/2, offset+w/2] span to a wall of length `len`,
// shrinking the width if the wall is shorter than the opening. Returns
// { offsetM, widthM } or null when the wall can't hold the minimum opening.
function fitToWall(offsetM, widthM, len) {
  const w = Math.min(widthM, len)
  if (w < MIN_OPENING_M) return null
  const half = w / 2
  const off = Math.max(half, Math.min(len - half, offsetM))
  return { offsetM: off, widthM: w }
}

// Resolve an opening to its local-frame geometry on `graph`:
//   { center:[x,y], a:[x,y], b:[x,y], ux, uy, widthM }
// null when its wall is gone/degenerate. Endpoints reflect the wall-clamped
// width, so a window never overhangs a corner.
export function openingSegment(graph, opening, byId = vById(graph)) {
  const we = wallEnds(graph, opening.wallId, byId)
  if (!we) return null
  const fit = fitToWall(opening.offsetM, opening.widthM, we.len)
  if (!fit) return null
  const cx = we.a[0] + we.ux * fit.offsetM
  const cy = we.a[1] + we.uy * fit.offsetM
  const half = fit.widthM / 2
  return {
    center: [cx, cy],
    a: [cx - we.ux * half, cy - we.uy * half],
    b: [cx + we.ux * half, cy + we.uy * half],
    ux: we.ux,
    uy: we.uy,
    widthM: fit.widthM,
  }
}

// Perpendicular distance and clamped along-wall offset of point (x,y) on a wall.
function projectOntoWall(we, x, y) {
  const t = (x - we.a[0]) * we.ux + (y - we.a[1]) * we.uy
  const tc = Math.max(0, Math.min(we.len, t))
  const px = we.a[0] + we.ux * tc
  const py = we.a[1] + we.uy * tc
  return { offsetM: tc, dist: Math.hypot(x - px, y - py) }
}

// Nearest wall to a point. Returns { wallId, offsetM, len, dist } or null when
// none is within `tol` metres.
export function nearestWall(graph, x, y, tol = Infinity) {
  const byId = vById(graph)
  let best = null
  let bestD = tol
  for (const w of graph?.walls ?? []) {
    const we = wallEnds(graph, w.id, byId)
    if (!we) continue
    const pr = projectOntoWall(we, x, y)
    if (pr.dist < bestD) {
      bestD = pr.dist
      best = { wallId: w.id, offsetM: pr.offsetM, len: we.len, dist: pr.dist }
    }
  }
  return best
}

export function nextOpeningId(openings = []) {
  let max = 0
  for (const o of openings) {
    const m = /^op(\d+)$/.exec(o?.id ?? "")
    if (m) max = Math.max(max, Number(m[1]))
  }
  return `op${max + 1}`
}

// Add an opening at the wall nearest (x,y). Returns a NEW openings array with
// the opening appended, or the ORIGINAL array unchanged when no wall is within
// `tol` or the wall is too short for the minimum opening.
export function placeOpening(graph, openings = [], x, y, kind = "window", tol = 0.6) {
  const near = nearestWall(graph, x, y, tol)
  if (!near) return openings
  const fit = fitToWall(near.offsetM, DEFAULT_WIDTH[kind] ?? WINDOW_WIDTH_M, near.len)
  if (!fit) return openings
  return [
    ...openings,
    { id: nextOpeningId(openings), wallId: near.wallId, offsetM: fit.offsetM, widthM: fit.widthM, kind },
  ]
}

export function removeOpening(openings = [], id) {
  return openings.filter((o) => o.id !== id)
}

export function updateOpening(openings = [], id, patch) {
  return openings.map((o) => (o.id === id ? { ...o, ...patch } : o))
}

// Re-attach openings after a structural graph edit (see file header). `oldGraph`
// is the graph the openings were last resolved against; `newGraph` is the edited
// one. Openings whose wall vanished and that don't land on any new wall are
// dropped (e.g. the wall they were on was deleted).
export function reconcileOpenings(newGraph, oldGraph, openings = []) {
  const byIdNew = vById(newGraph)
  const byIdOld = vById(oldGraph)
  const out = []
  for (const o of openings) {
    const newWE = wallEnds(newGraph, o.wallId, byIdNew)
    const oldWE = wallEnds(oldGraph, o.wallId, byIdOld)
    if (newWE && oldWE) {
      // Same wall still exists — a vertex move may have stretched/shifted it.
      // Keep the opening on it at the same RATIO so it tracks the wall.
      const ratio = oldWE.len > 1e-9 ? o.offsetM / oldWE.len : 0.5
      const fit = fitToWall(ratio * newWE.len, o.widthM, newWE.len)
      if (fit) {
        out.push({ ...o, offsetM: fit.offsetM, widthM: fit.widthM })
        continue
      }
    }
    // Wall id gone (split/renamed by normalize) → re-anchor by old centre.
    const seg = oldWE ? openingSegment(oldGraph, o, byIdOld) : null
    if (!seg) continue
    const near = nearestWall(newGraph, seg.center[0], seg.center[1], ATTACH_TOL_M)
    if (!near) continue
    const fit = fitToWall(near.offsetM, o.widthM, near.len)
    if (!fit) continue
    out.push({ ...o, wallId: near.wallId, offsetM: fit.offsetM, widthM: fit.widthM })
  }
  return out
}

// ---- window → insolation coupling (Stage 9c) ----------------------------

// Direct-sun hours a surface receives, given its OUTWARD unit normal in the
// plot frame (x east, y south) and daylight sun samples. A window admits direct
// sun when the sun is on its outward side: dot(normal, sunDir) > 0, where the
// horizontal direction toward the sun is (−sin A, cos A) for SunCalc azimuth A
// (measured from south toward west — see sun.js). Each sample represents
// `stepMinutes` of daylight; unweighted so the result reads as plain "hours of
// sun the window faces", comparable to the insolationLevel thresholds.
export function windowSunHours(normalEastSouth, sunSamples = [], stepMinutes = 15) {
  const [nx, ny] = normalEastSouth
  const nlen = Math.hypot(nx, ny)
  if (nlen < 1e-9) return 0
  let minutes = 0
  for (const s of sunSamples) {
    const sx = -Math.sin(s.azimuth)
    const sy = Math.cos(s.azimuth)
    if ((nx * sx + ny * sy) / nlen > 0) minutes += stepMinutes
  }
  return minutes / 60
}

// Outward unit normal of an opening segment relative to a room: the
// perpendicular whose small step OUT of the opening leaves the room polygon.
// Robust to a concave/L room whose area-weighted centroid lands outside the
// polygon (a side-pick by centroid would then flip the wrong way). Falls back
// to the first candidate when neither probe is clearly inside the room (e.g. the
// opening centre isn't on this room's boundary). Local plan frame.
export function openingOutwardNormal(seg, roomPolygon) {
  const cands = [[-seg.uy, seg.ux], [seg.uy, -seg.ux]]
  for (const [nx, ny] of cands) {
    const inX = seg.center[0] - nx * 0.05
    const inY = seg.center[1] - ny * 0.05
    // Inward probe lands inside the room → this candidate points outward.
    if (pointInPolygon(inX, inY, roomPolygon)) return [nx, ny]
  }
  return cands[0]
}

function pointInPolygon(x, y, polygon) {
  let inside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0], yi = polygon[i][1]
    const xj = polygon[j][0], yj = polygon[j][1]
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

function distPointSeg(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay
  const len2 = dx * dx + dy * dy
  if (len2 === 0) return Math.hypot(px - ax, py - ay)
  let t = ((px - ax) * dx + (py - ay) * dy) / len2
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy))
}

const ON_EDGE_TOL_M = 0.25

// True when point p lies on the boundary of `polygon` (within tolerance).
function onPolygonBoundary(p, polygon, tol = ON_EDGE_TOL_M) {
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    if (distPointSeg(p[0], p[1], polygon[j][0], polygon[j][1], polygon[i][0], polygon[i][1]) <= tol) return true
  }
  return false
}

function rotateVec([x, y], deg) {
  const r = (deg * Math.PI) / 180
  const c = Math.cos(r)
  const s = Math.sin(r)
  return [x * c - y * s, x * s + y * c]
}

// Best direct-sun exposure (hours) among a room's EXTERIOR windows, plus a
// count. A window belongs to the room when its centre lies on the room's
// boundary; it is EXTERIOR when a short step along its outward normal leaves the
// house footprint (an interior window between two rooms admits no outdoor sun).
// All polygons/openings are in the LOCAL plan frame; `rotationDeg` maps the
// outward normal into the plot (east/south) frame for windowSunHours.
//
// Returns { hours, windows } — hours is 0 when the room has no exterior window.
export function roomWindowSun({ roomPolygon, footprint, openings, graph, rotationDeg = 0, sunSamples, stepMinutes = 15 }) {
  let hours = 0
  let windows = 0
  const byId = vById(graph)
  for (const o of openings ?? []) {
    if (o.kind !== "window") continue
    const seg = openingSegment(graph, o, byId)
    if (!seg) continue
    if (!onPolygonBoundary(seg.center, roomPolygon)) continue
    const n = openingOutwardNormal(seg, roomPolygon)
    // Exterior check: a small step outward must leave the footprint.
    const probe = [seg.center[0] + n[0] * 0.3, seg.center[1] + n[1] * 0.3]
    if (Array.isArray(footprint) && footprint.length >= 3 && pointInPolygon(probe[0], probe[1], footprint)) continue
    windows++
    const h = windowSunHours(rotateVec(n, rotationDeg), sunSamples, stepMinutes)
    if (h > hours) hours = h
  }
  return { hours, windows }
}
